/**
 * v-lazy-img 指令
 * 用法：<img v-lazy-img="card.imageUrl" />
 *
 * 功能：
 * 1. 懒加载（IntersectionObserver）
 * 2. 加载中显示占位图
 * 3. 加载失败显示占位图
 * 4. 占位图根据当前语言自动切换（zh-TW → no-img-tw.png，en → no-img-en.png，其余 → no-img-jp.png）
 */

import Vue from 'vue'
import i18n from '@/i18n'

import noImgEn from '@/assets/images/no-img-en.png'
import noImgTw from '@/assets/images/no-img-tw.png'
import noImgJp from '@/assets/images/no-img-jp.png'

function getPlaceholder () {
  const locale = i18n.locale
  if (locale === 'zh-TW') return noImgTw
  if (locale === 'en') return noImgEn
  return noImgJp
}

// 全局单例 IntersectionObserver
let observer = null

function getObserver () {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target
            const src = el.dataset.lazySrc
            if (!src) return

            // 预加载
            const tempImg = new Image()
            tempImg.onload = () => {
              el.src = src
              el.classList.remove('lazy-loading')
              el.classList.add('lazy-loaded')
            }
            tempImg.onerror = () => {
              el.src = getPlaceholder()
              el.classList.remove('lazy-loading')
              el.classList.add('lazy-error')
            }
            tempImg.src = src

            observer.unobserve(el)
          }
        })
      },
      { rootMargin: '100px' } // 提前 100px 开始加载
    )
  }
  return observer
}

function loadSrc (el, src) {
  el.src = getPlaceholder()
  el.classList.remove('lazy-loaded', 'lazy-error')
  el.classList.add('lazy-loading')

  const tempImg = new Image()
  tempImg.onload = () => {
    el.src = src
    el.classList.remove('lazy-loading')
    el.classList.add('lazy-loaded')
  }
  tempImg.onerror = () => {
    el.src = getPlaceholder()
    el.classList.remove('lazy-loading')
    el.classList.add('lazy-error')
  }
  tempImg.src = src
}

function isInViewport (el) {
  const rect = el.getBoundingClientRect()
  return (
    rect.top < (window.innerHeight + 100) &&
    rect.bottom > -100
  )
}

function bind (el, binding) {
  const src = binding.value

  // 先显示占位图
  el.src = getPlaceholder()
  el.classList.add('lazy-loading')

  if (!src) return

  el.dataset.lazySrc = src
  getObserver().observe(el)
}

function update (el, binding) {
  if (binding.value === binding.oldValue) return

  const src = binding.value

  // 取消旧的观察
  getObserver().unobserve(el)

  if (!src) {
    el.src = getPlaceholder()
    el.classList.remove('lazy-loaded', 'lazy-error')
    el.classList.add('lazy-loading')
    return
  }

  el.dataset.lazySrc = src

  // 已在视口内（如 CardDetail 切换卡牌）→ 直接加载，不走 Observer
  if (isInViewport(el)) {
    loadSrc(el, src)
  } else {
    el.src = getPlaceholder()
    el.classList.remove('lazy-loaded', 'lazy-error')
    el.classList.add('lazy-loading')
    getObserver().observe(el)
  }
}

function unbind (el) {
  getObserver().unobserve(el)
  delete el.dataset.lazySrc
}

Vue.directive('lazy-img', { bind, update, unbind })